/**
 * Stable machine readable error codes shared by HTTP, WebSocket and the
 * browser-side transfer engine. Codes are part of the public contract: never
 * rename one without bumping {@link PROTOCOL_VERSION}.
 */
export const ERROR_CODES = [
  // request / transport
  'BAD_REQUEST',
  'VALIDATION_FAILED',
  'UNSUPPORTED_VERSION',
  'UNKNOWN_MESSAGE',
  'MESSAGE_TOO_LARGE',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'NOT_FOUND',
  'METHOD_NOT_ALLOWED',
  'ORIGIN_NOT_ALLOWED',
  'RATE_LIMITED',
  // rooms
  'INVALID_ROOM_CODE',
  'ROOM_NOT_FOUND',
  'ROOM_FULL',
  'ROOM_EXPIRED',
  'ROOM_CLOSED',
  'UNAUTHORIZED',
  'ALREADY_IN_ROOM',
  'NOT_IN_ROOM',
  // peers / signaling
  'PEER_NOT_FOUND',
  'PEER_REMOVED',
  'SELF_TARGET',
  'SIGNALING_FAILED',
  // transfers
  'TRANSFER_REJECTED',
  'TRANSFER_CANCELLED',
  'TRANSFER_FAILED',
  'HASH_MISMATCH',
  'STORAGE_ERROR',
  'UNSUPPORTED',
  // fallback
  'INTERNAL_ERROR',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/** Short, safe, human readable defaults. Never include user input verbatim. */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  BAD_REQUEST: 'The request could not be understood.',
  VALIDATION_FAILED: 'The message did not match the protocol schema.',
  UNSUPPORTED_VERSION: 'This client speaks an unsupported protocol version.',
  UNKNOWN_MESSAGE: 'Unknown message type.',
  MESSAGE_TOO_LARGE: 'The message exceeds the maximum allowed size.',
  PAYLOAD_TOO_LARGE: 'The request body is too large.',
  UNSUPPORTED_MEDIA_TYPE: 'Expected an application/json request body.',
  NOT_FOUND: 'Resource not found.',
  METHOD_NOT_ALLOWED: 'Method not allowed for this route.',
  ORIGIN_NOT_ALLOWED: 'This origin is not allowed to connect.',
  RATE_LIMITED: 'Too many requests. Please slow down and try again.',
  INVALID_ROOM_CODE: 'Room codes are six characters, for example Q7M2XR.',
  ROOM_NOT_FOUND: 'That room does not exist or has already ended.',
  ROOM_FULL: 'This room has reached its device limit.',
  ROOM_EXPIRED: 'This room has expired.',
  ROOM_CLOSED: 'The host closed this room.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  ALREADY_IN_ROOM: 'You are already connected to this room.',
  NOT_IN_ROOM: 'You are not connected to this room.',
  PEER_NOT_FOUND: 'That device is no longer in the room.',
  PEER_REMOVED: 'The host removed this device from the room.',
  SELF_TARGET: 'A device cannot signal itself.',
  SIGNALING_FAILED: 'The signaling message could not be delivered.',
  TRANSFER_REJECTED: 'The receiving device rejected the transfer.',
  TRANSFER_CANCELLED: 'The transfer was cancelled.',
  TRANSFER_FAILED: 'The transfer failed.',
  HASH_MISMATCH: 'The received file failed its integrity check.',
  STORAGE_ERROR: 'Local storage is unavailable or full.',
  UNSUPPORTED: 'This browser does not support a required capability.',
  INTERNAL_ERROR: 'Something went wrong on our side.',
};

/** HTTP status mapping for API errors. */
export const ERROR_HTTP_STATUS: Record<ErrorCode, number> = {
  BAD_REQUEST: 400,
  VALIDATION_FAILED: 400,
  UNSUPPORTED_VERSION: 400,
  UNKNOWN_MESSAGE: 400,
  MESSAGE_TOO_LARGE: 413,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  ORIGIN_NOT_ALLOWED: 403,
  RATE_LIMITED: 429,
  INVALID_ROOM_CODE: 400,
  ROOM_NOT_FOUND: 404,
  ROOM_FULL: 409,
  ROOM_EXPIRED: 410,
  ROOM_CLOSED: 410,
  UNAUTHORIZED: 403,
  ALREADY_IN_ROOM: 409,
  NOT_IN_ROOM: 409,
  PEER_NOT_FOUND: 404,
  PEER_REMOVED: 403,
  SELF_TARGET: 400,
  SIGNALING_FAILED: 502,
  TRANSFER_REJECTED: 409,
  TRANSFER_CANCELLED: 409,
  TRANSFER_FAILED: 500,
  HASH_MISMATCH: 422,
  STORAGE_ERROR: 507,
  UNSUPPORTED: 501,
  INTERNAL_ERROR: 500,
};

export function httpStatusForError(code: ErrorCode): number {
  return ERROR_HTTP_STATUS[code] ?? 500;
}

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value);
}
